<template>
    <div class="dashboard-body-futuristic">
        <main class="futuristic-container">
            <AppSidebar activeItem="home" />

            <section class="dashboard-card-futuristic dashboard-card-scrollable">
                <div class="file-list-wrapper">
                    <div class="admin-header-premium">
                        <div class="tabs-modern">
                            <button :class="['tab-modern-btn', { active: activeTab === 'app' }]" @click="activeTab = 'app'">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                                App Alerts
                            </button>
                            <button :class="['tab-modern-btn', { active: activeTab === 'email' }]" @click="activeTab = 'email'">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                                Email Logs
                            </button>
                            <button :class="['tab-modern-btn', { active: activeTab === 'whatsapp' }]" @click="activeTab = 'whatsapp'">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                                WhatsApp Logs
                            </button>
                        </div>
                    </div>

                    <div class="admin-content-fade">
                        <div v-if="filteredLogs.length" class="list-head" aria-hidden="true">
                            <span>ID</span>
                            <span>Message / Subject</span>
                            <span>Reference</span>
                            <span>Timestamp</span>
                            <span>Status</span>
                        </div>

                        <div v-if="loading && !notifications.length" class="box-empty" role="status">
                            <p class="empty-title">Loading notifications…</p>
                        </div>

                        <div v-else-if="loadError" class="box-empty" role="alert">
                            <p class="empty-title">Couldn’t load notifications.</p>
                            <p class="empty-sub">Check your connection and try again.</p>
                            <button type="button" class="empty-btn" @click="fetchAllLogs">Retry</button>
                        </div>

                        <div v-else class="list-container">
                            <!-- A notification about a bid opens that bid; the rest are plain rows. -->
                            <component :is="log.pdf_id ? 'button' : 'div'" v-for="log in filteredLogs" :key="log.id"
                                :type="log.pdf_id ? 'button' : null"
                                :class="['list-item-card', { 'is-link': log.pdf_id }]"
                                @click="openLog(log)">
                                <span class="premium-id">#{{ log.id }}</span>
                                
                                <div class="user-meta-stack">
                                    <div class="user-visual" :style="getIconStyle()">
                                        <svg v-if="activeTab === 'app'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                                        <svg v-if="activeTab === 'email'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                                        <svg v-if="activeTab === 'whatsapp'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                                    </div>
                                    <div class="user-info-stack">
                                        <span class="user-name">{{ log.title || 'Notification' }}</span>
                                        <span class="stack-email tooltip" :title="log.message">{{ truncateString(log.message, 40) }}</span>
                                    </div>
                                </div>

                                <div class="data-pill">{{ log.pdf_id ? 'Bid #' + log.pdf_id : '—' }}</div>
                                <div class="premium-date">{{ formatDate(getTimestamp(log)) }}</div>

                                <div :class="['status-blob', getStatusClass(log)]">
                                    <span class="pulse-dot" aria-hidden="true"></span>
                                    {{ getStatusText(log) }}
                                </div>
                            </component>

                            <div v-if="!filteredLogs.length" class="box-empty">
                                <svg class="empty-icon" width="40" height="40" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
                                    stroke-linejoin="round" aria-hidden="true">
                                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                                </svg>
                                <p class="empty-title">{{ emptyTitle }}</p>
                                <p class="empty-sub">Updates about your bids, such as finished analyses, appear here.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    </div>
</template>

<script>
import axios from 'axios';
import AppSidebar from './Sidebar.vue';

export default {
    name: 'NotificationCenter',
    components: { AppSidebar },
    props: {
        getAccessToken: Function,
        headerVisiblity: Function
    },
    data() {
        return {
            activeTab: 'app',
            notifications: [],
            loading: false,
            loadError: false
        }
    },
    computed: {
        filteredLogs() {
            if (this.activeTab === 'app') {
                return this.notifications;
            } else if (this.activeTab === 'email') {
                return this.notifications.filter(n => n.email_sent);
            } else if (this.activeTab === 'whatsapp') {
                return this.notifications.filter(n => n.whatsapp_sent);
            }
            return [];
        },
        emptyTitle() {
            if (this.activeTab === 'email') return 'No email notifications.';
            if (this.activeTab === 'whatsapp') return 'No WhatsApp notifications.';
            return 'No notifications.';
        }
    },
    mounted() {
        this.headerVisiblity(true);
        this.fetchAllLogs();
    },
    methods: {
        async fetchAllLogs() {
            this.loading = true;
            this.loadError = false;
            try {
                // Hitting the updated endpoint that returns all notification columns
                const res = await axios.get(this.$API_URL + "/notifications", {
                    headers: { 'Authorization': 'Bearer ' + this.getAccessToken() },
                    suppressErrorAlert: true
                });
                this.notifications = Array.isArray(res.data) ? res.data : [];
            } catch (error) {
                console.error("Failed to fetch notification logs", error);
                this.loadError = true;
            } finally {
                this.loading = false;
            }
        },
        // Same as the header bell: mark it read, then open the bid it is about.
        async openLog(log) {
            if (!log.pdf_id) return;
            if (!log.is_read) {
                try {
                    await axios.post(`${this.$API_URL}/notifications/${log.id}/read`, {}, {
                        headers: { 'Authorization': 'Bearer ' + this.getAccessToken() },
                        suppressErrorAlert: true
                    });
                    log.is_read = true;
                } catch (e) {
                    // Opening the bid matters more than the read flag.
                    console.error("Failed to mark notification as read", e);
                }
            }
            this.$router.push({ name: 'BidDetails', params: { pdf_id: log.pdf_id } });
        },
        truncateString(str, num) {
            if (!str) return '';
            if (str.length <= num) return str;
            return str.slice(0, num) + '...';
        },
        getIconStyle() {
            if (this.activeTab === 'app') return 'background: #eff6ff; color: #3b82f6; border-color: #bfdbfe;';
            if (this.activeTab === 'email') return 'background: #fdf2f8; color: #ec4899; border-color: #fbcfe8;';
            if (this.activeTab === 'whatsapp') return 'background: #f0fdf4; color: #22c55e; border-color: #bbf7d0;';
            return '';
        },
        getTimestamp(log) {
            if (this.activeTab === 'email') return log.email_sent_at || log.created_date;
            if (this.activeTab === 'whatsapp') return log.whatsapp_sent_at || log.created_date;
            return log.created_date;
        },
        getStatusText(log) {
            if (this.activeTab === 'app') return log.is_read ? 'Read' : 'Unread';
            if (this.activeTab === 'email') return log.email_status || 'Pending';
            if (this.activeTab === 'whatsapp') return log.whatsapp_status || 'Pending';
            return 'Sent';
        },
        getStatusClass(log) {
            const status = this.getStatusText(log).toLowerCase();
            if (status === 'read' || status === 'delivered' || status === 'sent') return 'active';
            if (status === 'failed') return 'disabled';
            return 'pending'; // Requires new CSS class for Pending
        },
        formatDate(d) {
            if (!d) return '--';
            return new Date(d).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
            });
        }
    }
}
</script>

<style scoped>
/* Importing shared premium UI classes from CompanyAdmin design system */
.file-list-wrapper {
    position: relative;
    padding: 0 2rem 2rem 2rem;
    min-height: 100%;
    background: radial-gradient(circle at 0% 0%, rgba(79, 172, 254, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 100% 100%, rgba(0, 242, 254, 0.05) 0%, transparent 50%);
}

.admin-header-premium {
    display: flex;
    justify-content: center;
    margin-top: -1rem;
    margin-bottom: 2rem;
    position: sticky;
    top: -1rem;
    z-index: 100;
    pointer-events: none;
}

.tabs-modern {
    display: flex;
    position: relative;
    background: rgba(15, 23, 42, 0.8);
    backdrop-filter: blur(20px) saturate(180%);
    padding: 0.4rem;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    pointer-events: auto;
    gap: 0.25rem;
}

.tab-modern-btn {
    position: relative;
    padding: 0.75rem 1.5rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    border: none;
    background: transparent;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
    display: flex;
    align-items: center;
    gap: 0.75rem;
    z-index: 2;
}

.tab-modern-btn:hover { color: white; }
.tab-modern-btn.active { color: #4facfe; }
.tab-modern-btn.active svg {
    transform: translateY(-1px);
    color: #4facfe;
    filter: drop-shadow(0 0 5px rgba(79, 172, 254, 0.5));
}
.tab-modern-btn.active::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    z-index: -1;
}

.list-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    animation: staggerIn 0.6s ease-out;
}

.list-item-card {
    background: white;
    padding: 1.25rem 2rem;
    border-radius: 24px;
    border: 1px solid rgba(226, 232, 240, 0.8);
    display: grid;
    grid-template-columns: 80px 2fr 1fr 1fr 1fr;
    align-items: center;
    transition: all 0.3s ease;
}

/* Row header: same grid as the rows below it. */
.list-head {
    display: grid;
    grid-template-columns: 80px 2fr 1fr 1fr 1fr;
    padding: 0 2rem 1rem 2rem;
    border-bottom: 2px solid #f1f5f9;
    margin-bottom: 1.5rem;
    text-align: left;
}

.list-head span {
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
}

/* Rows that open a bid are real buttons; reset the native button look. */
button.list-item-card {
    width: 100%;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
}

.list-item-card.is-link:hover {
    background: #f8fafc;
    border-color: #0071C5;
}

.list-item-card:focus-visible {
    outline: 2px solid #0071C5;
    outline-offset: 2px;
}

.premium-id { font-weight: 700; color: var(--text-muted); font-size: 0.8rem; }
.premium-date { font-weight: 600; color: #475569; font-size: 0.85rem; }

.user-meta-stack {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.user-visual {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
    box-shadow: 0 4px 10px rgba(0,0,0,0.05);
}

.user-info-stack {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.user-name { font-weight: 700; color: #0f172a; }
.stack-email { font-size: 0.8rem; color: #64748b; }

.data-pill {
    background: #f8fafc;
    padding: 0.5rem 1rem;
    border-radius: 100px;
    font-weight: 600;
    font-size: 0.85rem;
    color: #334155;
    width: fit-content;
}

.status-blob {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 1rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    width: fit-content;
}

.status-blob.active { background: #dcfce7; color: #166534; }
.status-blob.disabled { background: #fee2e2; color: #991b1b; }
.status-blob.pending { background: #fffbeb; color: #b45309; }

.pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
}

@keyframes staggerIn {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
}

.box-empty {
    padding: 4rem 2rem;
    text-align: center;
    background: white;
    border-radius: 16px;
    border: 2px dashed #e2e8f0;
}
.empty-icon { color: var(--text-muted); margin: 0 auto 1rem; display: block; }
.empty-title { margin: 0; font-weight: 600; color: var(--text-primary); font-size: var(--fs-body); }
.empty-sub { margin: 0.35rem 0 0; color: var(--text-secondary); font-size: var(--fs-sm); }
.empty-btn {
    margin-top: 1rem;
    padding: 0.5rem 1.1rem;
    border-radius: 10px;
    border: 1px solid #0071C5;
    background: #fff;
    color: #0071C5;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
}
.empty-btn:hover { background: #f0f7fd; }

@media (prefers-reduced-motion: reduce) {
    .list-container { animation: none; }
    .list-item-card, .tab-modern-btn { transition: none; }
}
</style>
